import { useCallback, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import api, { fmtErr } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { toast } from "sonner";
import { useTranslation } from "react-i18next";
import TranslationUsage from "../components/admin/TranslationUsage";

const TABS = ["overview","waitlist","reports","users","venues","activity"];
const TAB_KEYS = {overview:"admin.tabOverview",waitlist:"admin.tabWaitlist",reports:"admin.tabReports",users:"admin.tabUsers",venues:"admin.tabVenues",activity:"admin.tabActivity"};

export default function Admin() {
  const { user } = useAuth();
  const { t } = useTranslation();
  const nav = useNavigate();
  const [tab, setTab] = useState("overview");

  useEffect(() => {
    if (user && user.role !== "admin" && user.role !== "moderator") nav("/scan");
  }, [user, nav]);

  if (!user || (user.role !== "admin" && user.role !== "moderator")) return null;

  return (
    <div className="min-h-screen bg-[#050505] text-white">
      <header className="px-6 py-4 border-b border-white/5 flex items-center justify-between">
        <div className="flex items-center gap-2">
          <span className="w-2.5 h-2.5 rounded-full bg-[#E60000]" />
          <span className="font-display text-xl tracking-tighter">{t("admin.title")}</span>
        </div>
        <button onClick={()=>nav("/scan")} className="text-sm text-zinc-400 hover:text-white" data-testid="admin-exit">{t("admin.exit")}</button>
      </header>

      <div className="px-6 py-4 flex gap-2 border-b border-white/5 overflow-x-auto">
        {TABS.map(tb => (
          <button key={tb} onClick={()=>setTab(tb)} data-testid={`admin-tab-${tb}`}
            className={`px-4 py-2 rounded-full font-mono-accent ${tab===tb?"bg-[#E60000] text-white":"bg-white/5 text-zinc-400 hover:text-white"}`}>{t(TAB_KEYS[tb])}</button>
        ))}
      </div>

      <div className="p-6 max-w-6xl mx-auto">
        {tab === "overview" && <Overview />}
        {tab === "waitlist" && <Waitlist />}
        {tab === "reports" && <Reports />}
        {tab === "users" && <Users />}
        {tab === "venues" && <Venues />}
        {tab === "activity" && <ActivityLog />}
      </div>
    </div>
  );
}

function Overview() {
  const { t } = useTranslation();
  const [a, setA] = useState(null);
  useEffect(()=>{ api.get("/admin/analytics").then(r=>setA(r.data)).catch(e=>toast.error(fmtErr(e))); }, []);
  if (!a) return <p className="text-zinc-500">{t("admin.loading")}</p>;
  const items = [
    [t("admin.totalUsers"), a.total_users],
    [t("admin.venues"), a.total_venues],
    [t("admin.waitlist"), a.waitlist],
    [t("admin.activePresence"), a.active_presence],
    [t("admin.matchesTotal"), a.matches_total],
    [t("admin.matches24h"), a.matches_24h],
    [t("admin.iminTaps24h"), a.interests_24h],
    [t("admin.messages24h"), a.messages_24h],
    [t("admin.openReports"), a.open_reports],
    [t("admin.newUsers7d"), a.new_users_7d],
  ];
  return (
    <div className="space-y-4" data-testid="admin-overview">
      <div className="grid grid-cols-2 md:grid-cols-5 gap-3">
        {items.map(([k,v]) => (
          <div key={k} className="glass rounded-2xl p-5">
            <div className="font-mono-accent text-zinc-500">{k}</div>
            <div className="font-display text-3xl mt-2">{v}</div>
          </div>
        ))}
      </div>
      <TranslationUsage />
    </div>
  );
}

function Waitlist() {
  const { t } = useTranslation();
  const [items, setItems] = useState([]);
  const [city, setCity] = useState("");
  // Effect owns its own loader closure — `load` is intentionally not lifted
  // out of the effect because it depends solely on `city` (already in deps).
  useEffect(() => {
    let cancelled = false;
    (async () => {
      const { data } = await api.get(`/admin/waitlist${city?`?city=${city}`:""}`);
      if (!cancelled) setItems(data.items);
    })();
    return () => { cancelled = true; };
  }, [city]);
  const exportCsv = () => {
    const cols = ["name","email","phone","instagram","city","interest_type","created_at"];
    const csv = [cols.join(",")].concat(items.map(i => cols.map(c => `"${(i[c]||"").toString().replace(/"/g,'""')}"`).join(","))).join("\n");
    const blob = new Blob([csv], { type:"text/csv" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a"); a.href = url; a.download = "waitlist.csv"; a.click();
  };
  return (
    <div data-testid="admin-waitlist">
      <div className="flex gap-2 mb-4">
        <select value={city} onChange={e=>setCity(e.target.value)} className="bg-[#1A1A1A] border border-white/10 rounded-xl px-3 py-2" data-testid="wl-city-filter">
          <option value="">{t("admin.allCities")}</option>
          {["NYC","LA","Miami","Austin","Chicago","Other"].map(c=><option key={c}>{c}</option>)}
        </select>
        <button onClick={exportCsv} data-testid="export-csv" className="bg-[#E60000] hover:bg-[#FF1A1A] px-4 py-2 rounded-xl text-sm font-semibold">{t("admin.exportCsv")}</button>
        <span className="ml-auto text-zinc-500 self-center">{t("admin.signups",{count:items.length})}</span>
      </div>
      <div className="glass rounded-2xl overflow-hidden">
        <table className="w-full text-sm">
          <thead className="text-left bg-white/5"><tr><th className="p-3">{t("admin.colName")}</th><th className="p-3">{t("admin.colEmail")}</th><th className="p-3">{t("admin.colCity")}</th><th className="p-3">{t("admin.colType")}</th><th className="p-3">{t("admin.colJoined")}</th></tr></thead>
          <tbody>
            {items.map(i => (
              <tr key={i.id} className="border-t border-white/5">
                <td className="p-3">{i.name}</td>
                <td className="p-3 text-zinc-400">{i.email}</td>
                <td className="p-3">{i.city}</td>
                <td className="p-3 text-zinc-400">{i.interest_type}</td>
                <td className="p-3 text-zinc-500 font-mono-accent">{(i.created_at||"").slice(0,10)}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

function Reports() {
  const { t } = useTranslation();
  const [items, setItems] = useState([]);
  const load = async () => { const { data } = await api.get("/admin/reports"); setItems(data.reports); };
  useEffect(()=>{ load(); }, []);
  const act = async (id, action) => { await api.post(`/admin/reports/${id}/resolve?action=${action}`); load(); };
  return (
    <div data-testid="admin-reports" className="space-y-3">
      {items.length === 0 && <p className="text-zinc-500">{t("admin.noReports")}</p>}
      {items.map(r => (
        <div key={r.id} className="glass rounded-2xl p-4" data-testid={`report-${r.id}`}>
          <div className="flex justify-between mb-2">
            <span className="font-mono-accent text-[#E60000]">{r.status}</span>
            <span className="text-xs text-zinc-500">{r.created_at?.slice(0,16)}</span>
          </div>
          <div className="text-sm">{t("admin.reporter")}: <code className="text-zinc-400">{r.reporter_id}</code></div>
          <div className="text-sm">{t("admin.target")}: <code className="text-zinc-400">{r.target_id}</code></div>
          <div className="text-sm mt-1">{t("admin.reason")}: <span className="text-white">{r.reason}</span></div>
          {r.context && <div className="text-xs text-zinc-500 mt-1">{r.context}</div>}
          {r.status === "open" && (
            <div className="flex gap-2 mt-3">
              <button onClick={()=>act(r.id,"warn")} data-testid={`warn-${r.id}`} className="px-3 py-1.5 rounded-full border border-white/20 text-xs">{t("admin.warn")}</button>
              <button onClick={()=>act(r.id,"ban")} data-testid={`ban-${r.id}`} className="px-3 py-1.5 rounded-full bg-[#E60000] text-xs font-semibold">{t("admin.ban")}</button>
              <button onClick={()=>act(r.id,"dismiss")} data-testid={`dismiss-${r.id}`} className="px-3 py-1.5 rounded-full border border-white/10 text-zinc-400 text-xs">{t("admin.dismiss")}</button>
            </div>
          )}
        </div>
      ))}
    </div>
  );
}

function Users() {
  const { t } = useTranslation();
  const [q, setQ] = useState("");
  const [items, setItems] = useState([]);
  // NOTE (intentional): `load` reads current `q` from closure, but this effect
  // runs only on mount so the initial fetch has empty `q`. Live search happens
  // via the Search button (calls load() directly) — NOT on every keystroke.
  const load = useCallback(async () => {
    const { data } = await api.get(`/admin/users${q?`?q=${q}`:""}`);
    setItems(data.users);
  }, [q]);
  useEffect(() => {
    // Initial mount only. Do not depend on `load` (would re-fire on every keystroke).
    api.get(`/admin/users`).then(r => setItems(r.data.users)).catch(() => { /* admin route auth-gated; UI handles */ });
  }, []);
  const ban = async (id) => { await api.post(`/admin/users/${id}/ban`); load(); };
  const unban = async (id) => { await api.post(`/admin/users/${id}/unban`); load(); };
  const setRole = async (id, role) => {
    try { await api.post(`/admin/users/${id}/role`, { role }); toast.success(t("admin.roleUpdated")); load(); }
    catch (e) { toast.error(fmtErr(e)); }
  };
  return (
    <div data-testid="admin-users">
      <div className="flex gap-2 mb-4">
        <input value={q} onChange={e=>setQ(e.target.value)} placeholder={t("admin.searchPlaceholder")} className="flex-1 bg-[#1A1A1A] border border-white/10 rounded-xl px-3 py-2" data-testid="user-search" />
        <button onClick={load} className="bg-[#E60000] px-4 rounded-xl text-sm font-semibold" data-testid="user-search-btn">{t("admin.search")}</button>
      </div>
      <div className="glass rounded-2xl overflow-hidden">
        <table className="w-full text-sm">
          <thead className="text-left bg-white/5"><tr><th className="p-3">{t("admin.colHandle")}</th><th className="p-3">{t("admin.colEmail")}</th><th className="p-3">{t("admin.colRole")}</th><th className="p-3">{t("admin.colStatus")}</th><th className="p-3">{t("admin.colAction")}</th></tr></thead>
          <tbody>
            {items.map(u => (
              <tr key={u.id} className="border-t border-white/5">
                <td className="p-3">@{u.handle}</td>
                <td className="p-3 text-zinc-400">{u.email}</td>
                <td className="p-3">
                  <select value={u.role} onChange={e=>setRole(u.id, e.target.value)} data-testid={`role-${u.id}`}
                    className="bg-[#1A1A1A] border border-white/10 rounded-lg px-2 py-1 text-xs font-mono-accent">
                    {["user","moderator","admin"].map(r=><option key={r} value={r}>{r}</option>)}
                  </select>
                </td>
                <td className="p-3">{u.banned ? <span className="text-[#FF1A1A]">{t("admin.banned")}</span> : <span className="text-green-400">{t("admin.active")}</span>}</td>
                <td className="p-3">
                  {u.banned
                    ? <button onClick={()=>unban(u.id)} data-testid={`unban-${u.id}`} className="text-xs font-mono-accent text-green-400">{t("admin.unban")}</button>
                    : <button onClick={()=>ban(u.id)} data-testid={`ban-${u.id}`} className="text-xs font-mono-accent text-[#E60000]">{t("admin.banUpper")}</button>}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

function Venues() {
  const { t } = useTranslation();
  const [items, setItems] = useState([]);
  const [form, setForm] = useState({ name:"", address:"", lat:0, lng:0, radius_m:75, city:"NYC", verified:true });
  const load = async () => { const { data } = await api.get("/venues"); setItems(data.venues); };
  useEffect(()=>{ load(); }, []);
  const create = async (e) => {
    e.preventDefault();
    try {
      await api.post("/venues", { ...form, lat: Number(form.lat), lng: Number(form.lng), radius_m: Number(form.radius_m) });
      toast.success(t("admin.venueAdded")); setForm({ name:"", address:"", lat:0, lng:0, radius_m:75, city:"NYC", verified:true }); load();
    } catch (e) { toast.error(fmtErr(e)); }
  };
  return (
    <div data-testid="admin-venues" className="grid md:grid-cols-2 gap-6">
      <form onSubmit={create} className="glass rounded-2xl p-5 space-y-3" data-testid="venue-form">
        <h3 className="font-display text-xl">{t("admin.addVenue")}</h3>
        <input data-testid="v-name" required value={form.name} onChange={e=>setForm({...form,name:e.target.value})} placeholder={t("admin.fName")} className="adm-input" />
        <input data-testid="v-address" required value={form.address} onChange={e=>setForm({...form,address:e.target.value})} placeholder={t("admin.fAddress")} className="adm-input" />
        <div className="grid grid-cols-2 gap-2">
          <input data-testid="v-lat" required type="number" step="0.0001" value={form.lat} onChange={e=>setForm({...form,lat:e.target.value})} placeholder={t("admin.fLat")} className="adm-input" />
          <input data-testid="v-lng" required type="number" step="0.0001" value={form.lng} onChange={e=>setForm({...form,lng:e.target.value})} placeholder={t("admin.fLng")} className="adm-input" />
        </div>
        <input data-testid="v-radius" type="number" value={form.radius_m} onChange={e=>setForm({...form,radius_m:e.target.value})} placeholder={t("admin.fRadius")} className="adm-input" />
        <select data-testid="v-city" value={form.city} onChange={e=>setForm({...form,city:e.target.value})} className="adm-input">
          {["NYC","LA","Miami","Austin","Chicago","Other"].map(c=><option key={c}>{c}</option>)}
        </select>
        <label className="flex items-center gap-2 text-sm"><input type="checkbox" checked={form.verified} onChange={e=>setForm({...form,verified:e.target.checked})} /> {t("admin.verified")}</label>
        <button data-testid="v-submit" className="bg-[#E60000] hover:bg-[#FF1A1A] py-3 rounded-full font-semibold w-full">{t("admin.addVenue")}</button>
      </form>
      <div className="space-y-2">
        <h3 className="font-display text-xl">{t("admin.verifiedVenues")}</h3>
        {items.map(v => (
          <div key={v.id} className="glass rounded-2xl p-4" data-testid={`venue-row-${v.id}`}>
            <div className="font-semibold">{v.name}</div>
            <div className="text-xs text-zinc-500">{v.address} · {v.city} · r={v.radius_m}m</div>
          </div>
        ))}
      </div>
      <style>{`.adm-input{width:100%;background:rgba(0,0,0,0.5);border:1px solid rgba(255,255,255,0.1);border-radius:12px;padding:10px 14px;color:white}.adm-input:focus{outline:none;border-color:#E60000}`}</style>
    </div>
  );
}


const ACTION_META = {
  "account.password_change": { label: "Password changed", color: "text-amber-400" },
  "account.password_reset":  { label: "Password reset (recovery)", color: "text-amber-400" },
  "account.delete":          { label: "Account deleted", color: "text-zinc-400" },
  "admin.user.ban":          { label: "User banned", color: "text-[#FF1A1A]" },
  "admin.user.unban":        { label: "User unbanned", color: "text-green-400" },
  "admin.user.role_change":  { label: "Role changed", color: "text-purple-400" },
  "admin.venue.create":      { label: "Venue created", color: "text-sky-400" },
  "admin.venue.update":      { label: "Venue updated", color: "text-sky-400" },
  "admin.venue.delete":      { label: "Venue deleted", color: "text-[#FF1A1A]" },
  "admin.report.resolve":    { label: "Report resolved", color: "text-emerald-400" },
};

function whoLabel(p) {
  if (!p) return "system";
  if (p.handle) return `@${p.handle}${p.role && p.role !== "user" ? ` · ${p.role}` : ""}`;
  return (p.id || "—").slice(0, 8);
}

function metaSummary(action, meta) {
  if (!meta || !Object.keys(meta).length) return null;
  if (action === "admin.user.role_change") return `${meta.from} → ${meta.to}`;
  if (action.startsWith("admin.venue")) return meta.name || null;
  if (action === "admin.report.resolve") return meta.resolution ? `resolution: ${meta.resolution}` : null;
  return null;
}

function ActivityLog() {
  const { t } = useTranslation();
  const [data, setData] = useState(null);
  const [filter, setFilter] = useState("");
  const load = useCallback(async () => {
    try {
      const { data } = await api.get(`/admin/audit-log${filter ? `?action=${filter}` : ""}`);
      setData(data);
    } catch (e) { toast.error(fmtErr(e)); }
  }, [filter]);
  useEffect(() => { load(); }, [load]);

  if (!data) return <p className="text-zinc-500" data-testid="activity-loading">{t("admin.loading")}</p>;

  const intact = data.integrity?.intact;
  return (
    <div data-testid="admin-activity">
      <div className="flex flex-wrap items-center gap-3 mb-4">
        <h3 className="font-display text-xl">{t("admin.activityTitle")}</h3>
        <span
          data-testid="activity-integrity"
          className={`px-2.5 py-1 rounded-full text-[11px] font-mono-accent border ${intact ? "border-green-500/40 text-green-400 bg-green-500/10" : "border-[#FF1A1A]/50 text-[#FF1A1A] bg-[#FF1A1A]/10"}`}>
          {intact ? t("admin.chainIntact") : t("admin.chainBroken")}
        </span>
        <select value={filter} onChange={e=>setFilter(e.target.value)} data-testid="activity-filter"
          className="ml-auto bg-[#1A1A1A] border border-white/10 rounded-xl px-3 py-2 text-sm">
          <option value="">{t("admin.allActions")}</option>
          {(data.actions || []).map(a => <option key={a} value={a}>{(ACTION_META[a]?.label) || a}</option>)}
        </select>
      </div>

      <p className="text-xs text-zinc-500 mb-4">{t("admin.activityNote")}</p>

      <div className="glass rounded-2xl overflow-hidden">
        <table className="w-full text-sm">
          <thead className="text-left bg-white/5">
            <tr>
              <th className="p-3">{t("admin.colWhen")}</th>
              <th className="p-3">{t("admin.colAction")}</th>
              <th className="p-3">{t("admin.colActor")}</th>
              <th className="p-3">{t("admin.colTarget")}</th>
              <th className="p-3">{t("admin.colDetails")}</th>
            </tr>
          </thead>
          <tbody>
            {data.entries.length === 0 && (
              <tr><td colSpan={5} className="p-6 text-center text-zinc-500" data-testid="activity-empty">{t("admin.activityEmpty")}</td></tr>
            )}
            {data.entries.map(e => {
              const m = ACTION_META[e.action] || { label: e.action, color: "text-zinc-300" };
              const summary = metaSummary(e.action, e.metadata);
              return (
                <tr key={e.id} className="border-t border-white/5 align-top" data-testid={`activity-row-${e.seq}`}>
                  <td className="p-3 text-zinc-400 whitespace-nowrap">{new Date(e.timestamp).toLocaleString()}</td>
                  <td className={`p-3 font-mono-accent ${m.color}`}>{m.label}</td>
                  <td className="p-3 text-zinc-300">{whoLabel(e.actor)}</td>
                  <td className="p-3 text-zinc-400">{e.target ? whoLabel(e.target) : "—"}</td>
                  <td className="p-3 text-zinc-500">{summary || "—"}</td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
      <p className="mt-3 text-[11px] text-zinc-600 font-mono-accent" data-testid="activity-count">
        {t("admin.showingEntries", { count: data.entries.length })}
      </p>
    </div>
  );
}
